import React, { ComponentProps, useCallback, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import cn from 'classnames'
import isEmpty from 'lodash/isEmpty'
import { ReactSortable } from 'react-sortablejs'

import { Tile, Category, Role, TileSectionCategory, Order, Subtile } from './types'

import { TileComponent } from './Tile'
import { CategoryTiles } from './CategoryTiles'

import { useLocalStorage } from './useStorage'
import { useDebouncedState } from './useDebouncedState'
import { Icons } from './Icons'

const orderLabels: { [key in Order]: string } = {
  [Order.POPULAR]: 'Popular',
  [Order.AZ]: 'A-Z',
  [Order.RECENT]: 'Latest',
  [Order.USER]: 'Your Order',
}
interface Props {
  theme?: string
  sectionIdentifier: string
  tiles: { [key: Category['slug']]: Tile[] }
  extraTiles: Tile[]
  categories: TileSectionCategory[]
  roles: Role[]
}

const smallDown = window.matchMedia('(min-width: 1024px)')

const getScroll = () => window.pageYOffset || document.documentElement.scrollTop
const setScroll = (position: number) => window.scrollTo({ top: position })
const offsetScroll = (offset: number) => setScroll(getScroll() + offset)

export const filterRole = (role: Role) => (tile: Tile) => (tile.roles || []).map(({ id }) => id).includes(role.id)

export const sortTilesBy = (order: Order, userSort?: number[]) => {
  switch (order) {
    case Order.POPULAR:
      return (a: Tile, b: Tile) => (a.order || 0) - (b.order || 0)
    case Order.AZ:
      return (a: Tile, b: Tile) => (a.title || '').localeCompare(b.title || '')
    case Order.RECENT:
      return (a: Tile, b: Tile) => (b.created || '').localeCompare(a.created || '')
    case Order.USER:
      if (userSort) return sortByIndexList<Tile>(userSort)
    default:
      return (a: Tile, b: Tile) => 0
  }
}

export const sortByIndexList =
  <T extends { id: number }>(indexList: number[]) =>
  (a: T, b: T) =>
    indexList.indexOf(a.id) - indexList.indexOf(b.id)

const parseRegex = (str: string, type: string = 'gi') => {
  try {
    const regex = new RegExp(str, 'gi')
    return regex
  } catch (error) {
    return null
  }
}

const stringMatches = (body: string, search: string): number => {
  const regex = parseRegex(search, 'gi')
  if (!regex) return body.toLocaleLowerCase().includes(search.toLocaleLowerCase()) ? 1 : 0
  const matches = body.matchAll(regex)
  let count = 0
  for (let match of matches) count++
  return count
}

const tileMatches = (tile: Tile | NonNullable<Subtile['tile']>, search: string): number => {
  let count = 0
  if (tile.title) count += stringMatches(tile.title, search)
  if (tile.tag) count += stringMatches(tile.tag, search)
  if (tile.content) count += stringMatches(tile.content, search)
  if ('subtiles' in tile) count += subtileMatches(tile.subtiles!, search)
  return count
}

const subtileMatches = (subtiles: Subtile[], search: string): number => {
  let count = 0
  for (let subtile of subtiles) {
    if (subtile.title) count += stringMatches(subtile.title, search)
    else if (subtile.link && subtile.link.title) count += stringMatches(subtile.link.title, search)
    if (subtile.tile) count += tileMatches(subtile.tile, search)
  }
  return count
}

const searchTiles = (tiles: Tile[], search: string): Tile[] => {
  const resultCounts: { [key: number]: number } = tiles.reduce((obj, tile) => ({ ...obj, [tile.id]: 0 }), {})
  for (let tile of tiles) resultCounts[tile.id] += tileMatches(tile, search)
  return tiles.filter((tile) => resultCounts[tile.id] > 0).sort((a, b) => resultCounts[b.id] - resultCounts[a.id])
}

const getCategoryIdFromElemRefs = <Elem extends HTMLElement>(
  elem: Elem,
  refs: { [id: number]: Elem }
): number | null => {
  let categoryId: number | null = null
  Object.entries(refs).forEach(([id, ref]) => {
    if (elem === ref) categoryId = Number(id)
  })
  if (categoryId === null) console.log('could not find category id for elem', elem)
  return categoryId
}

const ToolboxTiles: React.FC<Props> = ({
  theme,
  sectionIdentifier,
  tiles = [],
  categories = [],
  roles = [],
  extraTiles = [],
}) => {
  const allTiles = Object.values(tiles)
    .reduce((acc, tiles) => acc.concat(tiles), [])
    .concat(extraTiles)

  const [order, setOrder] = useLocalStorage<Order>(sectionIdentifier + '_orderBy', Order.POPULAR)
  const [roleFilter, setRoleFilter] = useLocalStorage<string>(sectionIdentifier + '_roleFilter', '')
  const [categoriesSortData, setCategoriesSortData] = useLocalStorage<string>(
    `${sectionIdentifier}__categories`,
    categories.map((cat) => `cat-${cat.id}`).join('|')
  )
  const [categoryExpandedState, setCategoryExpandedState] = useLocalStorage<{ [key: number]: boolean }>(
    `${sectionIdentifier}__categoriesExpanded`,
    categories.reduce((obj, category) => ({ ...obj, [category.id]: category._joinData.expanded }), {})
  )

  const [searchInputText, searchText, setSearchText] = useDebouncedState<string>('')
  const [searchResults, setSearchResults] = useState<Tile[]>([])
  const showSearchResults = searchText.length > 0
  const selectedRole = roleFilter ? roles.find((role) => String(role.id) === roleFilter) : undefined

  const categoryElems = useRef<{ [key: number]: HTMLDivElement }>({})
  const dragItemInitialPosition = useRef<DOMRect>(new DOMRect())
  const initialTouchScrollPosition = useRef<number>(getScroll())
  const postCollapseItemPosition = useRef<DOMRect>(new DOMRect())
  const preSortExpandedStates = useRef<{ [key: string]: boolean }>({})
  const lastClickedCategory = useRef<[HTMLDivElement | null, number]>([null, 0])

  useEffect(() => {
    let results = allTiles.sort(sortTilesBy(order))
    // if (selectedRole) results = results.filter(filterRole(selectedRole))
    if (searchText) results = searchTiles(results, searchText)
    setSearchResults(results)
  }, [searchText, roleFilter, roles])

  const handleReset = useCallback(() => {
    ReactDOM.unstable_batchedUpdates(() => {
      setOrder(Order.POPULAR)
      setRoleFilter('')
      setSearchText('')
      setCategoryExpandedState(
        categories.reduce((obj, category) => ({ ...obj, [category.id]: category._joinData.expanded }), {})
      )
    })
  }, [categories])

  const handleCategorySelect: ComponentProps<typeof ReactSortable>['onChoose'] = (evt) => {
    // Store the initial position of the draggable item before drag start trigger is fired
    const elem = evt.item as HTMLDivElement
    dragItemInitialPosition.current = elem.getBoundingClientRect()
    initialTouchScrollPosition.current = getScroll()

    // disable animations
    Object.values(categoryElems.current).forEach((elem) => elem.classList.add('disable-animation'))
  }

  const handleCategoryDragStart: ComponentProps<typeof ReactSortable>['onStart'] = (evt) => {
    // Store section expanded state prior to dragging, then collapse all
    preSortExpandedStates.current = {}

    Object.entries(categoryElems.current).forEach(([id, elem]) => {
      console.log(id, elem)
      preSortExpandedStates.current[id] = elem.classList.contains('expanded')
      elem.classList.remove('expanded')
    })

    // Offset scroll position so that now-collapsed item is the same as where it was before bulk collapse repaint
    const elem = evt.item as HTMLDivElement
    const verticalOffset = dragItemInitialPosition.current.top - elem.getBoundingClientRect().top
    offsetScroll(-verticalOffset)
    postCollapseItemPosition.current = elem.getBoundingClientRect()
  }

  const handleCategoryPointerUp: ComponentProps<typeof ReactSortable>['onUnchoose'] = (evt) => {
    const elem = evt.item as HTMLDivElement
    const itemWasDragged = !isEmpty(preSortExpandedStates.current)
    if (!itemWasDragged) {
      // this is to prevent running twice on mobile for some inexplicable reason
      if (lastClickedCategory.current[0] === elem && evt.timeStamp - lastClickedCategory.current[1] < 100) return
      // this is to prevent touch scroll from triggering a click
      if (Math.abs(getScroll() - initialTouchScrollPosition.current) > 12) {
        initialTouchScrollPosition.current = getScroll()
        return
      }
      const categoryId = getCategoryIdFromElemRefs(elem, categoryElems.current)
      if (categoryId !== null)
        setCategoryExpandedState({ ...categoryExpandedState, [categoryId]: !categoryExpandedState[categoryId] })
      lastClickedCategory.current = [elem, evt.timeStamp]
    }
  }

  const handleCategoryDrop: ComponentProps<typeof ReactSortable>['onEnd'] = (evt) => {
    // Store item position before re-expanding sections
    const elem = evt.item as HTMLDivElement
    const dragItemPosition = elem.getBoundingClientRect()

    // Re-expand sections based on state recorded in onStart
    if (!isEmpty(preSortExpandedStates.current)) {
      Object.entries(preSortExpandedStates.current)
        .filter(([, expanded]) => expanded)
        .forEach(([id]) => {
          const elem = categoryElems.current[Number(id)]
          if (elem) elem.classList.add('expanded')
        })
      // While this seems like a double up of logic it isn't -- won't run til after this function is done
      // Need to set class directly above in order to re-measure below
      setCategoryExpandedState(preSortExpandedStates.current)
      preSortExpandedStates.current = {}
    }

    // Calculate new scroll position so that item doesn't appear to vertically move once dropped
    const itemNewPosition = elem.getBoundingClientRect()
    const hasReordered = evt.oldIndex !== evt.newIndex
    const verticalOffset = itemNewPosition.top - dragItemInitialPosition.current.top
    const additionalOffset = hasReordered ? postCollapseItemPosition.current.top - dragItemPosition.top : 0
    offsetScroll(verticalOffset + additionalOffset)

    // turn animations back on
    Object.values(categoryElems.current).forEach((elem) => elem.classList.remove('disable-animation'))
    evt.stopPropagation()
  }

  return (
    <>
      <div
        className={cn('toolbox-tile-filters', 'flex', 'justify-content-space-between', theme && `background-${theme}`)}>
        <div className="flex-medium-up align-items-center">
          <label>Sort by</label>
          <div
            className="toolbox-tile-filters-inner sorting-filter-buttons flex align-items-center"
            style={showSearchResults ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
            {Object.entries(orderLabels).map(([ord, label]) => (
              <button
                key={ord}
                type="button"
                className={cn('button', 'button-xsmall', 'tile-order-button', ord === order && 'active')}
                onClick={() => setOrder(ord as Order)}
                children={label}
              />
            ))}
            <button type="button" className="button button-xsmall button-dark button-label filter-new-btn no-padding">
              <div className="shimmer shimmer-small button-padding-xsmall">
                <svg width="5" height="5" viewBox="0 0 5 5" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4.625 2.50002L0.875 4.66509L0.875 0.334961L4.625 2.50002Z" fill="white" />
                </svg>
                <span> NEW</span>
              </div>
            </button>
          </div>
        </div>

        <div className="spacer spacer-12 visible-small-up" />

        <div className="flex-medium-up align-items-center">
          <label className="filter-label hidden-xsmall">Filter by</label>
          <div className="toolbox-tile-filters-inner flex align-items-center">
            {roles.length > 0 && (
              <>
                <div className="selector">
                  <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                    <option value="">All Roles</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id} children={role.title} />
                    ))}
                  </select>
                </div>
              </>
            )}
            <div className="search-input">
              <input
                type="search"
                placeholder="Search tiles..."
                value={searchInputText}
                onChange={(e) => setSearchText(e.target.value || '')}
                className="input-small"
              />
              {!!searchInputText ? (
                <Icons.Close className="close-icon" onClick={() => setSearchText('')} />
              ) : (
                <Icons.Search />
              )}
            </div>
            <div className="spacer spacer-8 visible-xsmall" />

            <label className="hidden-xsmall">&nbsp;</label>
            <button type="button" className="button button-xsmall button-reset" onClick={() => handleReset()}>
              Reset
            </button>
          </div>
        </div>
      </div>
      {showSearchResults ? (
        <div className="toolbox-tiles-results-container">
          <div className="toolbox-tiles">
            {searchResults.length === 0 ? (
              <p style={{ margin: '1em 0 1em 0.75rem' }}>No results found!</p>
            ) : (
              <>
                {searchResults.map((tile) => (
                  <TileComponent key={tile.id} tile={tile} />
                ))}
              </>
            )}
          </div>
        </div>
      ) : (
        <>
          {selectedRole && !showSearchResults && (
            <CategoryTiles
              reportRef={(elem) => {} /*(categoryElems.current[0] = elem)*/}
              id={`${sectionIdentifier}_filter${roleFilter}`}
              expanded
              uncollapsible
              category={{ ...selectedRole, _joinData: { order: -1, expanded: true } }}
              tiles={allTiles}
              tileSort={order}
              onTileSortChange={setOrder}
              tileFilterRole={selectedRole}
            />
          )}
          <ReactSortable
            className="toolbox-tiles-categories-container"
            list={categoriesSortData.split('|').map((id) => ({ id }))}
            setList={(newState) => setCategoriesSortData(newState.map(({ id }) => id).join('|'))}
            group={sectionIdentifier}
            draggable={'[data-sortable-section]'}
            dataIdAttr={'data-sortable-section'}
            handle={'[data-sortable-section-handle]'}
            ghostClass={'dragging'}
            direction={'vertical'}
            animation={150}
            delay={smallDown.matches ? 0 : 100}
            onChoose={handleCategorySelect}
            onStart={handleCategoryDragStart}
            onUnchoose={handleCategoryPointerUp}
            onEnd={handleCategoryDrop}
            // onMove={evt => console.log('move', evt)}
            // onChange={evt => console.log('change', evt)}
            // onSort={evt => console.log('sort', evt)}
          >
            {categories
              .sort(
                sortByIndexList<TileSectionCategory>(
                  categoriesSortData.split('|').map((str) => parseInt(str.split('-')[1]))
                )
              )
              .map((category) => {
                const categoryIdentifier = `${sectionIdentifier}_${category.slug || category.id}`
                const expanded = categoryExpandedState[category.id]
                return (
                  <CategoryTiles
                    key={category.id}
                    reportRef={(elem) => (categoryElems.current[category.id] = elem)}
                    id={categoryIdentifier}
                    expanded={expanded}
                    category={category}
                    tiles={tiles[category.slug] || []}
                    tileSort={order}
                    onTileSortChange={setOrder}
                    // tileFilterRole={selectedRole}
                  />
                )
              })}
          </ReactSortable>
        </>
      )}
    </>
  )
}

window.ToolboxTiles = {
  init: (id, props) => {
    console.log('ToolboxTiles props', props)
    ReactDOM.render(<ToolboxTiles {...props} />, document.getElementById(id))
  },
}

interface CustomWindow extends Window {
  ToolboxTiles?: { init: (id: string, props: Props) => void }
}

declare const window: CustomWindow
